import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import { Botao, Cartao, Escala, GrupoEscolha, ItemCheck, TituloSecao } from '@/componentes/ui'
import { faseDoCiclo, formatarDataCompleta, hojeISO } from '@/lib/datas'
import { lerDiarioLocal, salvarDiarioLocal } from '@/lib/offline'
import { CHAVES, type ContextoPrograma, usePerfil } from '@/lib/sessao'
import { sincronizarDiarios } from '@/lib/sincronizacao'
import { supabase } from '@/lib/supabase'
import {
  ESTADOS_INTESTINO,
  NIVEIS_FOME,
  NIVEIS_HUMOR,
  NIVEIS_INCHACO,
  QUALIDADES_SONO,
  ROTULO_FASE_CICLO,
  ROTULO_FOME,
  ROTULO_HUMOR,
  ROTULO_INCHACO,
  ROTULO_INTESTINO,
  ROTULO_SONO,
} from '@/tipos/dominio'
import type { DiarioEntrada, Pratica } from '@/tipos/tabelas'

const ENTRADA_VAZIA: DiarioEntrada = {
  sono: null,
  energia: null,
  humor: null,
  fome: null,
  intestino: null,
  inchaco: null,
  copos_agua: 0,
  atividade_min: null,
  observacoes: null,
  praticas: [],
}

/**
 * Diário do dia (seção 10) — o diferencial declarado do programa.
 *
 * Regra que governa esta tela: nada é perdido. Toda alteração grava
 * imediatamente no IndexedDB e a tela sempre lê de lá. A subida para o
 * Supabase é assunto de segundo plano, com fila e reenvio. Ela preenche
 * isto às 21h, na cama, frequentemente sem sinal.
 */
export function PaginaDiario({ contexto }: { contexto: ContextoPrograma }) {
  const { matricula, dia } = contexto
  const hoje = hojeISO()

  const [entrada, setEntrada] = useState<DiarioEntrada>(ENTRADA_VAZIA)
  const [carregado, setCarregado] = useState(false)
  const [estadoSalvamento, setEstadoSalvamento] = useState<'salvo' | 'salvando' | 'pendente'>(
    'salvo',
  )

  const { data: praticas = [] } = useQuery({
    queryKey: CHAVES.praticas(matricula.turma.programa.id),
    queryFn: async () => {
      const { data, error } = await supabase
        .from('praticas')
        .select('*')
        .eq('programa_id', matricula.turma.programa.id)
        .eq('ativa', true)
        .lte('dia_inicio', dia)
        .order('ordem')

      if (error) throw error
      return data as Pratica[]
    },
    staleTime: 60 * 60 * 1000,
  })

  // Carrega do local primeiro; só busca no servidor se não houver nada.
  useEffect(() => {
    let ativo = true

    async function carregar() {
      const local = await lerDiarioLocal(matricula.id, hoje)
      if (!ativo) return

      if (local) {
        setEntrada(local.entrada)
        setEstadoSalvamento(local.estado === 'pendente' ? 'pendente' : 'salvo')
        setCarregado(true)
        return
      }

      const { data } = await supabase
        .from('diarios')
        .select('*, diario_praticas(pratica_id, concluida)')
        .eq('matricula_id', matricula.id)
        .eq('data', hoje)
        .maybeSingle()

      if (!ativo) return

      if (data) {
        const linha = data as DiarioEntrada & {
          diario_praticas: { pratica_id: string; concluida: boolean }[]
        }
        setEntrada({
          ...ENTRADA_VAZIA,
          ...linha,
          praticas: (linha.diario_praticas ?? [])
            .filter((p) => p.concluida)
            .map((p) => p.pratica_id),
        })
      }
      setCarregado(true)
    }

    void carregar()
    return () => {
      ativo = false
    }
  }, [matricula.id, hoje])

  // Grava local a cada mudança, com um respiro para não escrever a cada
  // tecla digitada no campo de observações.
  useEffect(() => {
    if (!carregado) return

    setEstadoSalvamento('salvando')
    const temporizador = setTimeout(async () => {
      await salvarDiarioLocal(matricula.id, hoje, dia, entrada)
      setEstadoSalvamento('pendente')
      const resultado = await sincronizarDiarios()
      setEstadoSalvamento(resultado.pendentes === 0 ? 'salvo' : 'pendente')
    }, 600)

    return () => clearTimeout(temporizador)
  }, [entrada, carregado, matricula.id, hoje, dia])

  function atualizar<C extends keyof DiarioEntrada>(campo: C, valor: DiarioEntrada[C]) {
    setEntrada((atual) => ({ ...atual, [campo]: valor }))
  }

  function alternarPratica(praticaId: string) {
    setEntrada((atual) => ({
      ...atual,
      praticas: atual.praticas.includes(praticaId)
        ? atual.praticas.filter((id) => id !== praticaId)
        : [...atual.praticas, praticaId],
    }))
  }

  return (
    <div className="flex flex-col gap-5 px-5 pt-6 pb-28">
      <header>
        <h1 className="font-titulo text-2xl font-bold">Diário de hoje</h1>
        <p className="mt-0.5 text-sm text-[var(--tinta-suave)]">{formatarDataCompleta(hoje)}</p>
        <IndicadorSalvamento estado={estadoSalvamento} />
      </header>

      <Cartao>
        <GrupoEscolha
          rotulo="Como foi seu sono?"
          opcoes={QUALIDADES_SONO.map((v) => ({ valor: v, rotulo: ROTULO_SONO[v] }))}
          valor={entrada.sono}
          aoEscolher={(v) => atualizar('sono', v)}
        />
      </Cartao>

      <Cartao>
        <Escala
          rotulo="Sua energia hoje"
          valor={entrada.energia}
          aoMudar={(v) => atualizar('energia', v)}
          descricaoMin="Sem energia"
          descricaoMax="Muita energia"
        />
      </Cartao>

      <Cartao>
        <GrupoEscolha
          rotulo="Seu humor"
          opcoes={NIVEIS_HUMOR.map((v) => ({
            valor: v,
            rotulo: ROTULO_HUMOR[v].rotulo,
            emoji: ROTULO_HUMOR[v].emoji,
          }))}
          valor={entrada.humor}
          aoEscolher={(v) => atualizar('humor', v)}
          colunas={3}
        />
      </Cartao>

      <Cartao>
        <GrupoEscolha
          rotulo="Sua fome"
          opcoes={NIVEIS_FOME.map((v) => ({ valor: v, rotulo: ROTULO_FOME[v] }))}
          valor={entrada.fome}
          aoEscolher={(v) => atualizar('fome', v)}
          colunas={3}
        />
      </Cartao>

      <Cartao className="flex flex-col gap-4">
        <GrupoEscolha
          rotulo="Intestino"
          opcoes={ESTADOS_INTESTINO.map((v) => ({ valor: v, rotulo: ROTULO_INTESTINO[v] }))}
          valor={entrada.intestino}
          aoEscolher={(v) => atualizar('intestino', v)}
          colunas={3}
        />
        <GrupoEscolha
          rotulo="Inchaço"
          opcoes={NIVEIS_INCHACO.map((v) => ({ valor: v, rotulo: ROTULO_INCHACO[v] }))}
          valor={entrada.inchaco}
          aoEscolher={(v) => atualizar('inchaco', v)}
          colunas={3}
        />
      </Cartao>

      <Cartao>
        <ContadorAgua
          copos={entrada.copos_agua}
          aoMudar={(copos) => atualizar('copos_agua', copos)}
        />
      </Cartao>

      <section>
        <TituloSecao>Práticas de hoje</TituloSecao>
        <div className="flex flex-col gap-2">
          {praticas.map((pratica) => (
            <ItemCheck
              key={pratica.id}
              titulo={pratica.nome}
              descricao={pratica.descricao}
              marcado={entrada.praticas.includes(pratica.id)}
              aoAlternar={() => alternarPratica(pratica.id)}
            />
          ))}
        </div>
      </section>

      <Cartao>
        <label className="block">
          <span className="mb-2 block font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
            Atividade física (minutos)
          </span>
          <input
            type="number"
            inputMode="numeric"
            min="0"
            max="600"
            value={entrada.atividade_min ?? ''}
            onChange={(e) => atualizar('atividade_min', e.target.valueAsNumber || null)}
            placeholder="0"
            className="h-13 w-full rounded-2xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-4 text-base"
          />
        </label>
      </Cartao>

      <Cartao>
        <label className="block">
          <span className="mb-2 block font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
            Observações
          </span>
          <textarea
            rows={4}
            value={entrada.observacoes ?? ''}
            onChange={(e) => atualizar('observacoes', e.target.value || null)}
            placeholder="Como foi seu dia? O que você percebeu no corpo?"
            className="w-full resize-none rounded-2xl border-2 border-[var(--borda)] bg-[var(--fundo)] p-4 text-base leading-relaxed"
          />
        </label>
      </Cartao>

      <SecaoCicloMenstrual matriculaId={matricula.id} userId={matricula.user_id} />
    </div>
  )
}

// ---------------------------------------------------------------------

function IndicadorSalvamento({ estado }: { estado: 'salvo' | 'salvando' | 'pendente' }) {
  const textos = {
    salvo: { texto: 'Tudo salvo', cor: 'text-verde' },
    salvando: { texto: 'Salvando…', cor: 'text-[var(--tinta-apagada)]' },
    pendente: { texto: 'Salvo no aparelho · envia quando tiver sinal', cor: 'text-dourado' },
  } as const

  return (
    <p className={`mt-2 text-xs font-medium ${textos[estado].cor}`} aria-live="polite">
      {textos[estado].texto}
    </p>
  )
}

function ContadorAgua({ copos, aoMudar }: { copos: number; aoMudar: (copos: number) => void }) {
  return (
    <div>
      <span className="mb-3 block font-titulo text-sm font-semibold text-[var(--tinta-suave)]">
        Água — copos de hoje
      </span>

      <div className="flex items-center justify-between gap-4">
        <Botao
          variante="contorno"
          tamanho="pequeno"
          onClick={() => aoMudar(Math.max(0, copos - 1))}
          aria-label="Remover um copo"
          className="size-12 shrink-0 p-0 text-2xl"
        >
          −
        </Botao>

        <div className="text-center">
          <span className="font-titulo text-4xl font-bold text-coral">{copos}</span>
          <span className="block text-xs text-[var(--tinta-apagada)]">
            {copos === 1 ? 'copo' : 'copos'}
          </span>
        </div>

        <Botao
          tamanho="pequeno"
          onClick={() => aoMudar(Math.min(30, copos + 1))}
          aria-label="Adicionar um copo"
          className="size-12 shrink-0 p-0 text-2xl"
        >
          +
        </Botao>
      </div>

      <div className="mt-3 flex flex-wrap gap-1" aria-hidden>
        {Array.from({ length: Math.min(copos, 15) }, (_, i) => (
          <span key={i} className="text-lg">
            💧
          </span>
        ))}
      </div>
    </div>
  )
}

/**
 * Ciclo menstrual (seção 10.1).
 *
 * A fase é derivada no cliente e o texto é apenas educativo — a
 * especificação é explícita em não trazer recomendação médica aqui.
 *
 * O toggle "Não menstruo" existe porque o registro já é opcional, mas
 * "opcional" sem um jeito de dizer isso continua parecendo um lembrete
 * diário para quem está no climatério, grávida ou em supressão
 * hormonal contínua — como se fosse esquecimento dela.
 */
function SecaoCicloMenstrual({
  matriculaId,
  userId,
}: {
  matriculaId: string
  userId: string
}) {
  const clienteQuery = useQueryClient()
  const { data: perfil } = usePerfil()
  const [data, setData] = useState<string>('')

  const { data: ultimoCiclo, refetch } = useQuery({
    queryKey: ['ciclo-menstrual', matriculaId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('ciclos_menstruais')
        .select('data_inicio')
        .eq('matricula_id', matriculaId)
        .order('data_inicio', { ascending: false })
        .limit(1)
        .maybeSingle()

      if (error) throw error
      return data as { data_inicio: string } | null
    },
  })

  const alternarAcompanhamento = useMutation({
    mutationFn: async (ativo: boolean) => {
      const { error } = await supabase
        .from('perfis')
        .update({ acompanha_ciclo: ativo })
        .eq('id', userId)
      if (error) throw error
    },
    onSuccess: () => void clienteQuery.invalidateQueries({ queryKey: CHAVES.perfil }),
  })

  async function registrar() {
    if (!data) return
    await supabase
      .from('ciclos_menstruais')
      .upsert(
        { matricula_id: matriculaId, data_inicio: data },
        { onConflict: 'matricula_id,data_inicio' },
      )
    setData('')
    await refetch()
  }

  const fase = ultimoCiclo ? faseDoCiclo(ultimoCiclo.data_inicio) : null
  // Padrão true: a coluna existe desde depois do lançamento, e quem já
  // usava o app não deve perder o card sem ter escolhido isso.
  const acompanha = perfil?.acompanha_ciclo ?? true

  if (!acompanha) {
    return (
      <Cartao className="flex items-center justify-between gap-3">
        <p className="text-sm text-[var(--tinta-suave)]">
          Você desativou o acompanhamento do ciclo menstrual.
        </p>
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          carregando={alternarAcompanhamento.isPending}
          onClick={() => alternarAcompanhamento.mutate(true)}
        >
          Reativar
        </Botao>
      </Cartao>
    )
  }

  return (
    <Cartao>
      <TituloSecao>Ciclo menstrual</TituloSecao>

      {fase && (
        <div className="mb-4 rounded-2xl bg-pink/10 p-4">
          <p className="font-titulo text-sm font-semibold text-pink">
            Fase {ROTULO_FASE_CICLO[fase.fase].nome} · dia {fase.diaDoCiclo}
          </p>
          <p className="mt-1 text-sm leading-relaxed text-[var(--tinta-suave)]">
            {ROTULO_FASE_CICLO[fase.fase].texto}
          </p>
        </div>
      )}

      <label className="block">
        <span className="mb-2 block text-sm text-[var(--tinta-suave)]">
          Primeiro dia da última menstruação
        </span>
        <div className="flex gap-2">
          <input
            type="date"
            value={data}
            max={hojeISO()}
            onChange={(e) => setData(e.target.value)}
            className="h-13 flex-1 rounded-2xl border-2 border-[var(--borda)] bg-[var(--fundo)] px-4 text-base"
          />
          <Botao tamanho="pequeno" disabled={!data} onClick={() => void registrar()}>
            Salvar
          </Botao>
        </div>
      </label>

      <div className="mt-3 flex items-center justify-between">
        <p className="text-xs text-[var(--tinta-apagada)]">
          Opcional. Serve só para te mostrar em que fase você está.
        </p>
        <button
          type="button"
          onClick={() => alternarAcompanhamento.mutate(false)}
          className="shrink-0 text-xs font-medium text-[var(--tinta-apagada)] underline decoration-dotted underline-offset-2"
        >
          Não menstruo
        </button>
      </div>
    </Cartao>
  )
}
